/**
 * 🔍 DOCUMENT QUALITY ANALYZER - Automated quality assessment for uploaded documents
 * Provides real-time quality scoring and issue detection for admin review
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  Stack,
  LinearProgress,
  Alert,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
  IconButton,
  Collapse,
  Badge,
} from '@mui/material';
import {
  CheckCircle,
  Warning,
  Error,
  Info,
  Visibility,
  Image,
  AspectRatio,
  Brightness6,
  Contrast,
  Blur,
  ExpandMore,
  ExpandLess,
  PhotoLibrary,
  Security,
  HighQuality,
} from '@mui/icons-material';

export interface QualityMetrics {
  overall: number; // 0-100
  resolution: {
    score: number;
    width: number;
    height: number;
    recommended: { width: number; height: number };
  };
  brightness: {
    score: number;
    level: 'too_dark' | 'too_bright' | 'optimal';
  };
  sharpness: {
    score: number;
    level: 'blurry' | 'fair' | 'sharp';
  };
  completeness: {
    score: number;
    issues: string[];
  };
  authenticity: {
    score: number;
    flags: string[];
  };
}

interface DocumentQualityAnalyzerProps {
  imageUrl: string;
  documentType: 'national_id' | 'driver_license' | 'vehicle_registration' | 'insurance' | 'selfie' | 'vehicle_photo';
  onQualityChange?: (metrics: QualityMetrics) => void;
  showDetails?: boolean;
  compact?: boolean;
}

const QUALITY_THRESHOLDS = {
  excellent: 90,
  good: 75,
  fair: 60,
  poor: 40
};

const DOCUMENT_REQUIREMENTS = {
  national_id: {
    minResolution: { width: 800, height: 600 },
    expectedElements: ['name', 'photo', 'id_number', 'signature'],
    description: 'National ID Card'
  },
  driver_license: {
    minResolution: { width: 800, height: 600 },
    expectedElements: ['name', 'photo', 'license_number', 'expiry_date'],
    description: 'Driver License'
  },
  vehicle_registration: {
    minResolution: { width: 800, height: 600 },
    expectedElements: ['vehicle_details', 'owner_name', 'plate_number'],
    description: 'Vehicle Registration'
  },
  insurance: {
    minResolution: { width: 800, height: 600 },
    expectedElements: ['policy_number', 'coverage_details', 'expiry_date'],
    description: 'Insurance Certificate'
  },
  selfie: {
    minResolution: { width: 400, height: 400 },
    expectedElements: ['face', 'clear_lighting'],
    description: 'Selfie Photo'
  },
  vehicle_photo: {
    minResolution: { width: 800, height: 600 },
    expectedElements: ['vehicle', 'plate_visible', 'clear_view'],
    description: 'Vehicle Photo'
  }
};

export const DocumentQualityAnalyzer: React.FC<DocumentQualityAnalyzerProps> = ({
  imageUrl,
  documentType,
  onQualityChange,
  showDetails = true,
  compact = false
}) => {
  const [metrics, setMetrics] = useState<QualityMetrics | null>(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [detailsExpanded, setDetailsExpanded] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);

  // Simulate quality analysis (in real implementation, this would call an ML service)
  const analyzeImageQuality = async (url: string): Promise<QualityMetrics> => {
    return new Promise((resolve) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      
      img.onload = () => {
        const requirements = DOCUMENT_REQUIREMENTS[documentType];
        
        // Simulate analysis metrics
        const resolution = {
          score: img.width >= requirements.minResolution.width && img.height >= requirements.minResolution.height ? 85 : 45,
          width: img.width,
          height: img.height,
          recommended: requirements.minResolution
        };

        // Simulate brightness analysis
        const brightness = {
          score: Math.random() * 40 + 60, // 60-100 range
          level: 'optimal' as const
        };

        // Simulate sharpness analysis
        const sharpnessScore = Math.random() * 30 + 70; // 70-100 range
        const sharpness = {
          score: sharpnessScore,
          level: sharpnessScore > 85 ? 'sharp' as const : sharpnessScore > 65 ? 'fair' as const : 'blurry' as const
        };

        // Simulate completeness check
        const completenessScore = Math.random() * 25 + 75; // 75-100 range
        const completeness = {
          score: completenessScore,
          issues: completenessScore < 80 ? ['Some text may be cut off', 'Poor lighting in corners'] : []
        };

        // Simulate authenticity check
        const authenticityScore = Math.random() * 20 + 80; // 80-100 range
        const authenticity = {
          score: authenticityScore,
          flags: authenticityScore < 85 ? ['Unusual shadows detected'] : []
        };

        const overall = Math.round((resolution.score + brightness.score + sharpness.score + completeness.score + authenticity.score) / 5);

        setTimeout(() => {
          const result: QualityMetrics = {
            overall,
            resolution,
            brightness,
            sharpness,
            completeness,
            authenticity
          };
          resolve(result);
        }, 1500); // Simulate analysis time
      };

      img.onerror = () => {
        resolve({
          overall: 0,
          resolution: { score: 0, width: 0, height: 0, recommended: requirements.minResolution },
          brightness: { score: 0, level: 'too_dark' },
          sharpness: { score: 0, level: 'blurry' },
          completeness: { score: 0, issues: ['Failed to load image'] },
          authenticity: { score: 0, flags: ['Image could not be analyzed'] }
        });
      };

      img.src = url;
    });
  };

  useEffect(() => {
    if (imageUrl && imageLoaded) {
      setIsAnalyzing(true);
      analyzeImageQuality(imageUrl).then((result) => {
        setMetrics(result);
        onQualityChange?.(result);
        setIsAnalyzing(false);
      });
    }
  }, [imageUrl, imageLoaded, documentType]);

  const getQualityLevel = (score: number): { level: string; color: string; icon: React.ReactNode } => {
    if (score >= QUALITY_THRESHOLDS.excellent) {
      return { level: 'Excellent', color: 'success', icon: <CheckCircle /> };
    } else if (score >= QUALITY_THRESHOLDS.good) {
      return { level: 'Good', color: 'success', icon: <CheckCircle /> };
    } else if (score >= QUALITY_THRESHOLDS.fair) {
      return { level: 'Fair', color: 'warning', icon: <Warning /> };
    } else if (score >= QUALITY_THRESHOLDS.poor) {
      return { level: 'Poor', color: 'error', icon: <Error /> };
    } else {
      return { level: 'Unreadable', color: 'error', icon: <Error /> };
    }
  };

  const getMetricIcon = (metricType: string) => {
    switch (metricType) {
      case 'resolution': return <AspectRatio />;
      case 'brightness': return <Brightness6 />;
      case 'sharpness': return <HighQuality />;
      case 'completeness': return <Visibility />;
      case 'authenticity': return <Security />;
      default: return <Info />;
    }
  };

  if (!imageUrl) {
    return (
      <Alert severity="info" icon={<PhotoLibrary />}>
        No image provided for quality analysis
      </Alert>
    );
  }

  if (compact) {
    return (
      <Box display="flex" alignItems="center" gap={1}>
        <img
          src={imageUrl}
          alt="Document"
          style={{ display: 'none' }}
          onLoad={() => setImageLoaded(true)}
          onError={() => setImageLoaded(true)}
        />
        {isAnalyzing ? (
          <Chip icon={<LinearProgress />} label="Analyzing..." size="small" />
        ) : metrics ? (
          <Chip
            icon={getQualityLevel(metrics.overall).icon}
            label={`${metrics.overall}% Quality`}
            color={getQualityLevel(metrics.overall).color as any}
            size="small"
          />
        ) : null}
      </Box>
    );
  }

  return (
    <Card variant="outlined">
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6" display="flex" alignItems="center" gap={1}>
            <Image />
            Document Quality Analysis
          </Typography>
          {showDetails && (
            <IconButton
              onClick={() => setDetailsExpanded(!detailsExpanded)}
              disabled={isAnalyzing || !metrics}
            >
              {detailsExpanded ? <ExpandLess /> : <ExpandMore />}
            </IconButton>
          )}
        </Box>

        {/* Hidden image to trigger loading */}
        <img
          src={imageUrl}
          alt="Document for analysis"
          style={{ display: 'none' }}
          onLoad={() => setImageLoaded(true)}
          onError={() => setImageLoaded(true)}
        />

        {isAnalyzing ? (
          <Box>
            <LinearProgress sx={{ mb: 2 }} />
            <Typography variant="body2" color="text.secondary" align="center">
              Analyzing {DOCUMENT_REQUIREMENTS[documentType].description.toLowerCase()}...
            </Typography>
          </Box>
        ) : metrics ? (
          <Box>
            {/* Overall Score */}
            <Box display="flex" alignItems="center" gap={2} mb={2}>
              <Box flex={1}>
                <Typography variant="body2" gutterBottom>Overall Quality</Typography>
                <LinearProgress
                  variant="determinate"
                  value={metrics.overall}
                  color={getQualityLevel(metrics.overall).color as any}
                  sx={{ height: 8, borderRadius: 1 }}
                />
              </Box>
              <Chip
                icon={getQualityLevel(metrics.overall).icon}
                label={`${metrics.overall}% ${getQualityLevel(metrics.overall).level}`}
                color={getQualityLevel(metrics.overall).color as any}
              />
            </Box>

            {/* Quick Issues Summary */}
            {(metrics.completeness.issues.length > 0 || metrics.authenticity.flags.length > 0) && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>Issues Detected:</Typography>
                {[...metrics.completeness.issues, ...metrics.authenticity.flags].map((issue, index) => (
                  <Typography key={index} variant="body2">• {issue}</Typography>
                ))}
              </Alert>
            )}

            {/* Detailed Metrics */}
            <Collapse in={detailsExpanded}>
              <Stack spacing={2} sx={{ mt: 2 }}>
                <Typography variant="subtitle2">Detailed Analysis:</Typography>
                
                {Object.entries({
                  resolution: `${metrics.resolution.width}×${metrics.resolution.height} (${metrics.resolution.score}%)`,
                  brightness: `${metrics.brightness.level.replace('_', ' ')} (${Math.round(metrics.brightness.score)}%)`,
                  sharpness: `${metrics.sharpness.level} (${Math.round(metrics.sharpness.score)}%)`,
                  completeness: `${Math.round(metrics.completeness.score)}% complete`,
                  authenticity: `${Math.round(metrics.authenticity.score)}% authentic`
                }).map(([key, value]) => (
                  <Box key={key} display="flex" alignItems="center" gap={1}>
                    {getMetricIcon(key)}
                    <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                      {key}:
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {value}
                    </Typography>
                  </Box>
                ))}

                {/* Recommendations */}
                {metrics.overall < QUALITY_THRESHOLDS.good && (
                  <Alert severity="info" sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>Recommendations:</Typography>
                    <List dense>
                      {metrics.resolution.score < 70 && (
                        <ListItem>
                          <ListItemIcon><AspectRatio fontSize="small" /></ListItemIcon>
                          <ListItemText primary="Use higher resolution camera or get closer to document" />
                        </ListItem>
                      )}
                      {metrics.brightness.score < 70 && (
                        <ListItem>
                          <ListItemIcon><Brightness6 fontSize="small" /></ListItemIcon>
                          <ListItemText primary="Improve lighting conditions" />
                        </ListItem>
                      )}
                      {metrics.sharpness.score < 70 && (
                        <ListItem>
                          <ListItemIcon><HighQuality fontSize="small" /></ListItemIcon>
                          <ListItemText primary="Hold camera steady and focus properly" />
                        </ListItem>
                      )}
                    </List>
                  </Alert>
                )}
              </Stack>
            </Collapse>
          </Box>
        ) : (
          <Alert severity="info">
            Click analyze to assess document quality
          </Alert>
        )}
      </CardContent>
    </Card>
  );
};